import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { STUDENT_NAV } from "@/pages/student/StudentHome";
import { ONET_ATTRIBUTION, ONET_URL, riasecMeta } from "@/lib/riasec";
import { OnetBadge } from "@/components/OnetBadge";
import { InterestResults } from "@/components/dashboard/InterestResults";
import { useAuth } from "@/context/AuthContext";
import { downloadInterestPdf } from "@/lib/pdf";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Compass, Loader2, ArrowRight, ArrowLeft, Save, RotateCcw, History,
  Sparkles, CheckCircle2, AlertTriangle, Share2, Copy, Trash2, Link2, FileDown, Briefcase,
} from "lucide-react";

const PER_PAGE_DESKTOP = 5;

function Attribution() {
  return (
    <div className="mt-6 rounded-lg bg-slate-50 px-4 py-3" data-testid="onet-attribution">
      <OnetBadge className="mb-2" />
      <p className="text-[11px] leading-relaxed text-slate-500">
      {ONET_ATTRIBUTION}{" "}
      <a href={ONET_URL} target="_blank" rel="noreferrer" className="font-semibold text-[#005291] underline hover:text-[#78be21]">O*NET Web Services</a>.
    </p>
    </div>
  );
}

// ---- Intro ---------------------------------------------------------------
function Intro({ available, hasProgress, latest, onStart, onResume, onViewResults, onHistory }) {
  return (
    <div className="mx-auto max-w-3xl">
      <Card className="p-8 card-elevate">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#003057] text-white"><Compass className="h-6 w-6" /></span>
          <div>
            <h2 className="font-heading text-xl font-extrabold text-[#003057]">O*NET Career Interest Profiler</h2>
            <p className="text-sm text-slate-500">30 quick questions · about 5–10 minutes</p>
          </div>
        </div>
        <p className="mt-5 text-sm leading-relaxed text-slate-600">
          Rate how much you'd enjoy different kinds of work. Your answers reveal your top
          <span className="font-semibold text-[#003057]"> RIASEC interest areas</span> and the careers that fit them.
          This is a measure of <span className="font-semibold">interests</span>, separate from your Transferable Skills Benchmark —
          your interest scores are private and never mixed with your TSB.
        </p>

        {!available && (
          <div className="mt-5 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="assessment-unavailable">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>The Career Interest Profile is temporarily unavailable. Please check back later.</span>
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          {latest && (
            <Button onClick={onViewResults} className="rounded-full bg-[#005291] font-semibold text-white hover:bg-[#003f70]" data-testid="view-results-btn">
              <Sparkles className="mr-2 h-4 w-4" /> View my results
            </Button>
          )}
          {available && hasProgress && (
            <Button onClick={onResume} className="rounded-full bg-[#78be21] font-semibold text-[#00243f] hover:bg-[#6aac1c]" data-testid="resume-btn">
              Resume where I left off <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
          {available && (
            <Button onClick={onStart} variant={latest || hasProgress ? "outline" : "default"}
              className={`rounded-full font-semibold ${latest || hasProgress ? "" : "bg-[#003057] text-white hover:bg-[#00243f]"}`}
              data-testid="start-assessment-btn">
              {latest ? <><RotateCcw className="mr-2 h-4 w-4" /> Retake assessment</> : "Start the assessment"}
            </Button>
          )}
          {latest && (
            <Button onClick={onHistory} variant="ghost" className="rounded-full font-semibold text-slate-500" data-testid="history-btn">
              <History className="mr-2 h-4 w-4" /> History
            </Button>
          )}
        </div>
        <Attribution />
      </Card>
    </div>
  );
}

// ---- Quiz ----------------------------------------------------------------
function Quiz({ questions, options, answers, setAnswers, perPage, onSubmit, onSaveExit, submitting }) {
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(questions.length / perPage);
  const start = page * perPage;
  const pageQs = questions.slice(start, start + perPage);
  const answeredCount = answers.filter((a) => a != null).length;
  const allAnswered = answeredCount === questions.length;
  const pageComplete = pageQs.every((q, i) => answers[start + i] != null);
  const isLast = page === totalPages - 1;

  const setAnswer = (idx, val) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[idx] = val;
      return next;
    });
  };

  const next = () => {
    if (!pageComplete) { toast.error("Please answer every question on this page."); return; }
    if (isLast) { onSubmit(); } else { setPage((p) => p + 1); window.scrollTo({ top: 0, behavior: "smooth" }); }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <Card className="p-6 sm:p-8 card-elevate">
        <div className="mb-5">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
            <span>Question {Math.min(start + 1, questions.length)}–{Math.min(start + perPage, questions.length)} of {questions.length}</span>
            <span data-testid="answered-count">{answeredCount}/{questions.length} answered</span>
          </div>
          <Progress value={(answeredCount / questions.length) * 100} className="mt-2 h-2" />
        </div>

        <p className="mb-4 text-sm text-slate-500">For each activity, choose how much you would enjoy it:</p>

        <div className="space-y-5">
          {pageQs.map((q, i) => {
            const idx = start + i;
            return (
              <div key={q.index ?? idx} className="rounded-xl border border-slate-200 p-4" data-testid={`question-${idx}`}>
                <p className="font-heading text-base font-semibold text-[#003057]">{q.text}</p>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-5">
                  {options.map((opt) => {
                    const selected = answers[idx] === opt.value;
                    return (
                      <button key={opt.value} type="button" onClick={() => setAnswer(idx, opt.value)}
                        data-testid={`q${idx}-opt${opt.value}`}
                        className={`rounded-lg border px-2 py-2 text-xs font-semibold transition-colors ${
                          selected ? "border-[#005291] bg-[#005291] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-[#005291]/50 hover:bg-slate-50"
                        }`}>
                        {opt.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-2">
            <Button variant="outline" disabled={page === 0} onClick={() => { setPage((p) => p - 1); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className="rounded-full font-semibold" data-testid="prev-page-btn">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
            <Button variant="ghost" onClick={onSaveExit} className="rounded-full font-semibold text-slate-500" data-testid="save-exit-btn">
              <Save className="mr-2 h-4 w-4" /> Save & finish later
            </Button>
          </div>
          <Button onClick={next} disabled={submitting || (isLast && !allAnswered)}
            className="rounded-full bg-[#78be21] font-semibold text-[#00243f] hover:bg-[#6aac1c]" data-testid="next-submit-btn">
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {isLast ? "See my results" : "Next"} {!isLast && <ArrowRight className="ml-2 h-4 w-4" />}
          </Button>
        </div>
        <p className="mt-3 text-center text-xs text-slate-400">Page {page + 1} of {totalPages}</p>
      </Card>
    </div>
  );
}

// ---- Manage Sharing ------------------------------------------------------
function ManageSharing() {
  const [shares, setShares] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/assessments/shares").then(({ data }) => setShares(data)).catch(() => setShares([]));
  useEffect(() => { load(); }, []);

  const linkFor = (token) => `${window.location.origin}/app/advisor/interest/${token}`;

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/assessments/shares");
      toast.success("Read-only link created.");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const copy = async (token) => {
    try { await navigator.clipboard.writeText(linkFor(token)); toast.success("Link copied to clipboard."); }
    catch { toast.error("Couldn't copy — select and copy the link manually."); }
  };

  const revoke = async (id) => {
    try { await api.delete(`/assessments/shares/${id}`); toast.success("Access revoked."); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const active = (shares || []).filter((s) => !s.revoked);

  return (
    <Card className="p-6 sm:p-8 card-elevate" data-testid="manage-sharing">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-[#003057]">
            <Share2 className="h-5 w-5 text-[#005291]" /> Share with an advisor
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            Create a private, read-only link. Only advisors and faculty can open it — never employers. Revoke anytime.
          </p>
        </div>
        <Button onClick={create} disabled={busy} className="rounded-full bg-[#003057] font-semibold text-white hover:bg-[#00243f]" data-testid="create-share-btn" data-pdf-hide>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2 className="mr-2 h-4 w-4" />} Create link
        </Button>
      </div>

      {shares === null && <p className="mt-4 text-sm text-slate-400">Loading…</p>}
      {shares && active.length === 0 && <p className="mt-4 text-sm text-slate-400" data-testid="no-shares">No active links. Create one to share your results.</p>}

      <div className="mt-4 space-y-2" data-testid="shares-list">
        {active.map((s) => (
          <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 p-3" data-testid={`share-${s.id}`}>
            <div className="min-w-0">
              <p className="truncate font-mono text-xs text-slate-600">{linkFor(s.token)}</p>
              <p className="text-[11px] text-slate-400">Created {s.created_at?.slice(0, 10)}</p>
            </div>
            <div className="flex gap-2" data-pdf-hide>
              <Button variant="outline" size="sm" onClick={() => copy(s.token)} className="rounded-full" data-testid={`copy-share-${s.id}`}>
                <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy
              </Button>
              <Button variant="ghost" size="sm" onClick={() => revoke(s.id)} className="rounded-full text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`revoke-share-${s.id}`}>
                <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Revoke
              </Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ---- Interest → Jobs -----------------------------------------------------
function InterestJobs() {
  const [state, setState] = useState({ loading: true, jobs: [] });
  useEffect(() => {
    api.get("/assessments/interest-jobs")
      .then(({ data }) => setState({ loading: false, jobs: data.jobs || [] }))
      .catch(() => setState({ loading: false, jobs: [] }));
  }, []);

  return (
    <Card className="p-6 sm:p-8 card-elevate" data-testid="interest-jobs">
      <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-[#003057]">
        <Briefcase className="h-5 w-5 text-[#78be21]" /> Jobs that match your interests
      </h3>
      <p className="mt-1 text-sm text-slate-500">Open Job Board postings ranked against your top interest areas.</p>

      {state.loading && <p className="mt-4 text-sm text-slate-400">Loading…</p>}
      {!state.loading && state.jobs.length === 0 && (
        <p className="mt-4 rounded-lg bg-slate-50 px-4 py-6 text-center text-sm text-slate-400" data-testid="interest-jobs-empty">
          No matching openings on the board yet. New postings will show up here as they're added.
        </p>
      )}
      {!state.loading && state.jobs.length > 0 && (
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="interest-jobs-list">
          {state.jobs.map((j) => {
            const m = riasecMeta(j.match_area);
            return (
              <Link key={j.id} to="/app/student/jobs"
                className="flex items-center justify-between rounded-xl border border-slate-200 p-4 card-elevate hover:border-[#005291]/50"
                data-testid={`interest-job-${j.id}`}>
                <div className="min-w-0">
                  <p className="truncate font-heading text-sm font-bold text-[#003057]">{j.title}</p>
                  <p className="truncate text-xs text-slate-400">{j.company}{j.industry ? ` · ${j.industry}` : ""}</p>
                  <span className="mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold text-white" style={{ backgroundColor: m.color }}>
                    Matches your {j.match_area} interest
                  </span>
                </div>
                <span className="ml-2 font-mono-num text-lg font-bold text-[#005291]">{j.interest_match}%</span>
              </Link>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ---- Results -------------------------------------------------------------
function Results({ result, name, onRetake, onHistory }) {
  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <Card className="flex flex-wrap items-center justify-between gap-3 p-6 card-elevate">
        <div>
          <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-[#003057]">
            <CheckCircle2 className="h-6 w-6 text-[#78be21]" /> Your Career Interests
          </h2>
          <p className="text-sm text-slate-500">Completed {result.created_at?.slice(0, 10)} · O*NET Interest Profiler</p>
        </div>
        <div className="flex gap-2" data-pdf-hide>
          <Button variant="outline" onClick={() => downloadInterestPdf(result, name)} className="rounded-full font-semibold" data-testid="results-pdf-btn">
            <FileDown className="mr-2 h-4 w-4" /> PDF
          </Button>
          <Button variant="outline" onClick={onHistory} className="rounded-full font-semibold" data-testid="results-history-btn">
            <History className="mr-2 h-4 w-4" /> History
          </Button>
          <Button variant="outline" onClick={onRetake} className="rounded-full font-semibold" data-testid="results-retake-btn">
            <RotateCcw className="mr-2 h-4 w-4" /> Retake
          </Button>
        </div>
      </Card>

      <InterestResults result={result} />
      <InterestJobs />
      <ManageSharing />
    </div>
  );
}

// ---- History -------------------------------------------------------------
function HistoryView({ items, onOpen, onBack }) {
  return (
    <div className="mx-auto max-w-3xl">
      <Card className="p-6 sm:p-8 card-elevate">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-[#003057]"><History className="h-6 w-6 text-[#005291]" /> Assessment History</h2>
          <Button variant="outline" onClick={onBack} className="rounded-full font-semibold" data-testid="history-back-btn"><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>
        </div>
        {items.length === 0 && <p className="mt-4 text-sm text-slate-400">No past assessments yet.</p>}
        <div className="mt-4 space-y-3" data-testid="history-list">
          {items.map((it) => (
            <button key={it.attempt_id} onClick={() => onOpen(it.attempt_id)}
              className="flex w-full items-center justify-between rounded-xl border border-slate-200 p-4 text-left hover:border-[#005291]/50 hover:bg-slate-50"
              data-testid={`history-item-${it.attempt_id}`}>
              <div>
                <p className="text-sm font-bold text-[#003057]">{it.created_at?.slice(0, 10)}</p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {(it.top3 || []).map((r, i) => {
                    const m = riasecMeta(r.title);
                    return <span key={i} className="rounded-full px-2 py-0.5 text-[11px] font-semibold text-white" style={{ backgroundColor: m.color }}>{r.title}</span>;
                  })}
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400" />
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ---- Page ----------------------------------------------------------------
export default function InterestProfile() {
  const { user } = useAuth();
  const [phase, setPhase] = useState("loading"); // loading|intro|quiz|results|history
  const [available, setAvailable] = useState(true);
  const [latest, setLatest] = useState(null);
  const [progress, setProgress] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [options, setOptions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [historyItems, setHistoryItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const perPage = useMediaPerPage();
  const loadedQuestions = useRef(false);

  useEffect(() => {
    (async () => {
      const [statusRes, latestRes, progRes] = await Promise.allSettled([
        api.get("/assessments/onet/status"),
        api.get("/assessments/latest"),
        api.get("/assessments/progress"),
      ]);
      setAvailable(statusRes.status === "fulfilled" ? statusRes.value.data.available : false);
      setLatest(latestRes.status === "fulfilled" && !latestRes.value.data.empty ? latestRes.value.data : null);
      const p = progRes.status === "fulfilled" ? progRes.value.data : null;
      setProgress(p && Array.isArray(p.answers) && p.answers.some((a) => a != null) ? p : null);
      setPhase("intro");
    })();
  }, []);

  const loadQuestions = async (initialAnswers) => {
    try {
      let qs = questions, opts = options;
      if (!loadedQuestions.current) {
        const { data } = await api.get("/assessments/questions");
        qs = data.question || [];
        opts = data.answer_option || [];
        setQuestions(qs);
        setOptions(opts);
        loadedQuestions.current = true;
      }
      setAnswers(initialAnswers && initialAnswers.length === qs.length ? initialAnswers : new Array(qs.length).fill(null));
      setPhase("quiz");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setAvailable(false);
      setPhase("intro");
    }
  };

  const startNew = () => loadQuestions(null);
  const resume = () => loadQuestions(progress?.answers);

  const viewResults = () => { if (latest) { setResult(latest); setPhase("results"); } };

  const saveExit = async () => {
    try {
      await api.post("/assessments/progress", { answers, page: 0 });
      toast.success("Progress saved. You can finish later.");
      setProgress({ answers });
      setPhase("intro");
    } catch { toast.error("Couldn't save your progress."); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/assessments/submit", { answers });
      setResult(data);
      setLatest(data);
      setProgress(null);
      setPhase("results");
      toast.success("Your interest profile is ready!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const openHistory = async () => {
    try {
      const { data } = await api.get("/assessments/history");
      setHistoryItems(data);
      setPhase("history");
    } catch { toast.error("Couldn't load history."); }
  };

  const openAttempt = async (id) => {
    try {
      const { data } = await api.get(`/assessments/${id}`);
      setResult(data);
      setPhase("results");
    } catch { toast.error("Couldn't open that assessment."); }
  };

  return (
    <DashboardShell title="Career Interest Profile" subtitle="O*NET Interest Profiler — your vocational interests (RIASEC)" nav={STUDENT_NAV}>
      {phase === "loading" && <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#003057]" /></div>}
      {phase === "intro" && (
        <Intro available={available} hasProgress={!!progress} latest={latest}
          onStart={startNew} onResume={resume} onViewResults={viewResults} onHistory={openHistory} />
      )}
      {phase === "quiz" && (
        <Quiz questions={questions} options={options} answers={answers} setAnswers={setAnswers}
          perPage={perPage} onSubmit={submit} onSaveExit={saveExit} submitting={submitting} />
      )}
      {phase === "results" && result && (
        <Results result={result} name={user?.name} onRetake={startNew} onHistory={openHistory} />
      )}
      {phase === "history" && (
        <HistoryView items={historyItems} onOpen={openAttempt} onBack={() => setPhase(latest ? "results" : "intro")} />
      )}
    </DashboardShell>
  );
}

function useMediaPerPage() {
  const [perPage, setPerPage] = useState(typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches ? PER_PAGE_DESKTOP : 1);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const handler = (e) => setPerPage(e.matches ? PER_PAGE_DESKTOP : 1);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return perPage;
}
